import React from 'react'
import styled from 'styled-components'
import {
  space,
  border,
  typography,
  shadow,
  flexbox,
  layout,
  SpaceProps,
  BorderProps,
  TypographyProps,
  ShadowProps,
  FlexboxProps,
  LayoutProps,
} from 'styled-system'
import { useSelector, useDispatch } from 'react-redux'
import * as userActions from '../../store/actions/userActions'
import { AnyAction } from 'redux'
import { ThunkDispatch } from 'redux-thunk'

// Define RootState type since we can't import it
interface RootState {
  user: {
    session_expired: boolean
    csrfCookie: string
    csrf_token: string
    user_settings: {
      dark_mode: boolean
    }
    darkMode: boolean
  }
}

type AppDispatch = ThunkDispatch<RootState, unknown, AnyAction>

type SwitchProps = SpaceProps & BorderProps & TypographyProps & ShadowProps & FlexboxProps & LayoutProps & {
  active?: boolean
  bg?: string
  theme?: {
    colors: Record<string, string>
  }
}

const SwitchStyled = styled.div<SwitchProps>`
  display: inline-block;
  cursor: pointer;
  width: 3.5rem;
  height: 33px;
  border-radius: 17px;
  transition: all 0.3s ease-out;
  background: ${({ active, theme, bg }: SwitchProps) =>
    active && theme?.colors && bg ? theme.colors[bg] : `#eae9f2`};
  position: relative;
  margin: 0 0.5rem;
  white-space: nowrap;
  ${border};
  ${space};
  ${typography};
  ${shadow};
  ${flexbox};
  ${layout};
`

type RoundProps = {
  active?: boolean
}

const Round = styled.span<RoundProps>`
  width: 21px;
  height: 21px;
  background-color: ${({ active }: RoundProps) => (active ? `#ffffff` : `#696969`)};
  position: absolute;
  transition: all 0.3s ease-out;
  left: ${({ active }: RoundProps) => (active ? `calc(100% - 33px)` : `0`)};
  margin-left: 6px;
  top: 0;
  margin-top: 6px;
  transition: 0.4s;
  border-radius: 500px;
  pointer-events: none;
`

interface SwitchComponentProps {
  bg?: string
  comingSoon?: boolean
  onClick?: () => void
}

const Switch: React.FC<SwitchComponentProps> = ({
  bg = 'secondary',
  comingSoon,
  onClick = () => {},
  ...rest
}) => {
  const dispatch = useDispatch<AppDispatch>()
  const userSessionExpired = useSelector((state: RootState) => state.user.session_expired)
  const csrfCookie = useSelector((state: RootState) => state.user.csrfCookie)
  const csrfCoinigy = useSelector((state: RootState) => state.user.csrf_token)
  const userSettings = useSelector((state: RootState) => state.user.user_settings)
  const userDarkModeSelector = useSelector((state: RootState) => state.user.darkMode)
  const userDarkModeSettings =
    typeof userSettings !== 'undefined' ? userSettings.dark_mode : false
  
  const csrfMatch = /coinigy\_csrf\_token\=([^$;]*)/gi.exec(document.cookie)
  const newToken = csrfMatch ? csrfMatch[1] : ''

  const handleSwitch = async () => {
    if (csrfCookie !== csrfCoinigy) {
      dispatch(userActions.fetchCsrf())
    }
    dispatch(userActions.setDarkMode(!userDarkModeSelector))
    if (userSessionExpired === false) {
      await dispatch(
        userActions.saveUserDarkMode(newToken, !userDarkModeSettings)
      )
      await dispatch(userActions.fetchUserSettings())
    }
    if (onClick) onClick()
  }

  if (comingSoon === true) {
    return (
      <SwitchStyled
        bg={bg}
        {...rest}
        onClick={handleSwitch}
        active={userDarkModeSelector}
      >
        <Round active={userDarkModeSelector} />
      </SwitchStyled>
    )
  }
  return (
    <SwitchStyled
      bg={bg}
      {...rest}
      onClick={handleSwitch}
      active={
        userSessionExpired === true
          ? userDarkModeSelector
          : userDarkModeSettings
      }
    >
      <Round
        active={
          userSessionExpired === true
            ? userDarkModeSelector
            : userDarkModeSettings
        }
      />
    </SwitchStyled>
  )
}

export default Switch
